import { Box, Button, Divider, Flex, Group, Modal, Stack, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import FinalizeReviewAnimation from './FinalizeReviewAnimation';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  opened: boolean;
  close: () => void;
  newCount: number;
  existingCount: number;
  unreviewedCount: number;
  onConfirm: () => Promise<void>;
};

const MINIMUM_ANIMATION_DURATION_MS = 1200;

export function FinalizeReviewModal({
  opened,
  close,
  newCount,
  existingCount,
  unreviewedCount,
  onConfirm,
}: Props) {
  const { client, clientId } = useCurrentClient();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleConfirm = async () => {
    const startTime = Date.now();
    requestAnimationFrame(async () => {
      setIsLoading(true);
      await onConfirm();
      const endTime = Date.now();
      const duration = endTime - startTime;
      const finish = () => navigate(`/client/${clientId}/document/add`);

      if (duration < MINIMUM_ANIMATION_DURATION_MS) {
        setTimeout(finish, MINIMUM_ANIMATION_DURATION_MS - duration);
        return;
      }

      finish();
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={
        <Text size="displayXs" fw={FONT_WEIGHTS.bold} c="gray.10">
          {getTitle(newCount, existingCount, client?.firstName)}
        </Text>
      }
    >
      <Stack gap={0}>
        <FinalizeReviewAnimation collapsed={isLoading} />

        <ProgressRow
          title="New data fields added"
          count={newCount}
          isSuccess={true}
          isNew={newCount > 0}
          mt="sm"
        />

        <ProgressRow
          title="Existing data fields updated"
          count={existingCount}
          isSuccess={true}
          isNew={existingCount > 0}
          mt="xs"
        />

        <Divider my="sm" />

        {unreviewedCount > 0 && (
          <>
            <ProgressRow
              title="Left unreviewed"
              count={unreviewedCount}
              isSuccess={false}
              isNew={false}
              mb="xxxs"
            />

            <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb="md">
              If you finalise now, you won&apos;t be able to review them later. To do so,
              you&apos;ll need to upload the document again.
            </Text>
          </>
        )}

        <Group gap="xs" grow>
          <Button variant="primary" size="xl" onClick={handleConfirm}>
            Finalise review
          </Button>
          <Button variant="secondary" size="xl" onClick={close}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function getTitle(newCount: number, existingCount: number, clientFirstName?: string) {
  const prefix = clientFirstName ? `${clientFirstName}'s record ` : 'Record';

  if (newCount === 0 && existingCount === 0) {
    return `${prefix} won't be updated - finalise anyway?`;
  }

  if (newCount + existingCount === 1) {
    return `${prefix} successfully updated - one step forward!`;
  }

  return `${prefix} successfully updated - solid work!`;
}

type ProgressRowProps = {
  title: string;
  count: number;
  isSuccess: boolean;
  isNew: boolean;
  mt?: string;
  mb?: string;
};

function ProgressRow({ title, count, isSuccess, isNew, mt, mb }: ProgressRowProps) {
  return (
    <Flex justify="space-between" mt={mt} mb={mb}>
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
        {title}
      </Text>
      <Flex align="center" gap="xxs">
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {count}
        </Text>
        <Box
          w={20}
          h={20}
          style={{
            borderRadius: '50%',
            backgroundColor: isNew ? 'var(--mantine-color-brand-6)' : 'var(--mantine-color-gray-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isSuccess ? (
            <IconCheck size={14} strokeWidth={4} color="white" />
          ) : (
            <IconX size={14} strokeWidth={4} color="white" />
          )}
        </Box>
      </Flex>
    </Flex>
  );
}
